<template>
  <div class="domain-picker">
    <div class="domain-picker__chips">
      <span v-for="domain in picked" :key="domain.id" class="domain-picker__chip">
        {{ domain.host }}
        <button type="button" :title="$t('satellites.domains.unassign')" @click="remove(domain.id)"><SatIcon name="x" :size="12" /></button>
      </span>
      <input
        v-model.trim="query"
        type="text"
        class="domain-picker__input"
        :placeholder="picked.length ? $t('satellites.domains.pick_more') : $t('satellites.domains.pick_placeholder')"
        @focus="focused = true"
        @blur="onBlur"
        @keydown.enter.prevent="addFirst"
        @keydown.backspace="onBackspace"
      />
    </div>
    <div v-if="focused && suggestions.length" class="domain-picker__suggestions">
      <button v-for="domain in suggestions" :key="domain.id" type="button" class="domain-picker__suggestion" @mousedown.prevent="add(domain.id)">
        <span class="domain-picker__host">{{ domain.host }}</span>
        <span v-if="domain.router && domain.router.id !== routerId" class="domain-picker__current">{{ $t("satellites.domains.now_on", { name: domain.router.name }) }}</span>
        <span v-else-if="!domain.router" class="domain-picker__current">{{ $t("satellites.domains.no_router") }}</span>
      </button>
    </div>
    <div v-if="moving.length" class="sat-note _warn mt-2">
      <SatIcon name="alert" />
      <span>{{ $t("satellites.domains.moving_note", { hosts: moving.map((d) => d.host).join(", ") }) }}</span>
    </div>
  </div>
</template>
<script>
import SatIcon from "@/components/Satellites/SatIcon.vue";

const SUGGESTIONS = 8;

// Domains of a router: v-model is a list of domain ids. Picking a domain of another router moves it to this one.
export default {
  name: "DomainPicker",
  components: { SatIcon },
  props: {
    value: {
      type: Array,
      required: true,
    },
    domains: {
      type: Array,
      required: true,
    },
    // The router being edited, null for a new one
    routerId: {
      type: String,
      default: null,
    },
  },
  data() {
    return { query: "", focused: false };
  },
  computed: {
    picked() {
      return this.value.map((id) => this.domains.find((d) => d.id === id)).filter(Boolean);
    },
    suggestions() {
      const query = this.query.toLowerCase();
      return this.domains.filter((d) => !this.value.includes(d.id) && (!query || d.host.includes(query))).slice(0, SUGGESTIONS);
    },
    moving() {
      return this.picked.filter((d) => d.router && d.router.id !== this.routerId);
    },
  },
  methods: {
    add(id) {
      this.$emit("input", [...this.value, id]);
      this.query = "";
    },
    remove(id) {
      this.$emit("input", this.value.filter((item) => item !== id));
    },
    addFirst() {
      if (this.suggestions.length) this.add(this.suggestions[0].id);
    },
    onBackspace() {
      if (!this.query && this.value.length) this.remove(this.value[this.value.length - 1]);
    },
    onBlur() {
      this.focused = false;
    },
  },
};
</script>
<style lang="scss" scoped>
.domain-picker__chips {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  min-height: 46px;
  padding: 6px 8px;
  border-radius: 10px;
  background: var(--sat-input);
}

.domain-picker__chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 6px 4px 10px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  color: var(--sat-accent);
  background: var(--sat-accent-soft);

  button {
    display: inline-flex;
    padding: 2px;
    border: 0;
    border-radius: 4px;
    color: inherit;
    background: transparent;
    cursor: pointer;

    &:hover {
      background: rgba(0, 0, 0, 0.06);
    }

    &:focus {
      outline: none;
    }
  }
}

.domain-picker__input {
  flex: 1;
  min-width: 160px;
  height: 32px !important;
  padding: 0 4px !important;
  border: 0 !important;
  background: transparent !important;
}

.domain-picker__suggestions {
  margin-top: 6px;
  padding: 4px;
  border: 1px solid var(--sat-border);
  border-radius: 10px;
  background: var(--sat-card);
}

.domain-picker__suggestion {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 10px;
  border: 0;
  border-radius: 8px;
  text-align: left;
  color: var(--sat-text);
  background: transparent;
  cursor: pointer;

  &:hover {
    background: var(--sat-hover);
  }

  &:focus {
    outline: none;
  }
}

.domain-picker__host {
  font-size: 14px;
  font-weight: 600;
}

.domain-picker__current {
  margin-left: auto;
  font-size: 12px;
  color: var(--sat-muted);
}
</style>
